import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useModal } from '../context/ModalContext';
import { solutionPages } from '../data/solutions';
import { services } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { openDemoModal } = useModal();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const serviceLinks = services.map((service) => ({
    id: service.id,
    slug: service.slug,
    name: service.label,
    summary: service.summary,
  }));

  const resourceLinks = [
    { name: 'Case Studies', href: '/#case-studies' },
    { name: 'Careers', href: '/careers' },
  ];
  const primarySolutionLink = {
    name: 'Custom AI Solutions',
    href: '/solutions/custom-ai-solutions',
  };
  const hiddenSolutionSlugs = new Set([
    'finance-services',
    'property-management',
    'talent-acquisition',
    'custom-ai-solutions',
  ]);
  const solutionLinks = solutionPages
    .filter((solution) => !hiddenSolutionSlugs.has(solution.slug))
    .map((solution) => ({
      name: solution.name,
      href: `/solutions/${solution.slug}`,
    }));
  const capabilityRoutes = {
    consulting: "/consulting",
    automation: "/automation",
    "agent-development": "/agent-development",
    "voice-ai": "/voice-ai",
    "product-development": "/product-development",
    "knowledge-rag": "/knowledge-rag",
    "ai-infrastructure": "/ai-infrastructure",
    "knowledge-infrastructure-management": "/knowledge-infrastructure-management",
  };

  const navigateToService = (service) => {
    const route = capabilityRoutes[service.slug];
    if (route) {
      window.location.href = route;
      return;
    }

    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      window.dispatchEvent(
        new CustomEvent('synentia:select-service', {
          detail: { serviceId: service.id },
        })
      );
      servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.location.href = `/?service=${service.slug}#services`;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[--bg-primary]/85 backdrop-blur-xl border-b border-[--border]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <a href="/" className="flex items-center gap-0">
            <span className="text-xl font-semibold text-[--text-primary] tracking-[-0.04em]">
              Synentia
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2">
            <div className="relative group">
              <button className="px-3 py-2 text-[11px] font-medium text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.18em] transition-colors font-mono inline-flex items-center gap-1.5">
                Capabilities
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 pointer-events-none translate-y-1 group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 transition-all duration-200">
                <div className="min-w-[360px] rounded-2xl border border-[--border] bg-[--bg-secondary] shadow-xl p-3">
                  <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.16em] font-mono text-[--text-muted]">
                    Service Tracks
                  </p>
                  <div className="space-y-1">
                    {serviceLinks.map((service) => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => navigateToService(service)}
                        className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[--bg-tertiary] transition-colors border border-transparent hover:border-[--border]"
                      >
                        <p className="text-sm font-medium text-[--text-primary]">{service.name}</p>
                        <p className="mt-0.5 text-xs text-[--text-secondary]">{service.summary}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button className="px-3 py-2 text-[11px] font-medium text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.18em] transition-colors font-mono inline-flex items-center gap-1.5">
                Solution
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 pointer-events-none translate-y-1 group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 transition-all duration-200">
                <div className="min-w-[560px] h-[360px] rounded-2xl border border-[--border] bg-[--bg-secondary] shadow-xl p-4 flex flex-col">
                  <div className="pb-3 border-b border-[--border]">
                    <p className="text-[10px] uppercase tracking-[0.16em] font-mono text-[--text-muted]">Solution Tracks</p>
                    <p className="mt-1 text-sm text-[--text-secondary]">Choose a focused implementation lane</p>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-1.5 content-start">
                    {solutionLinks.map((link) => (
                      <a
                        key={link.name}
                        href={link.href}
                        className="block px-3.5 py-2.5 rounded-xl text-sm font-medium text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-tertiary] transition-colors border border-transparent hover:border-[--border]"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 border-t border-[--border] flex justify-end">
                    <a
                      href={primarySolutionLink.href}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] hover:bg-[--accent-hover] transition-colors btn-pattern"
                    >
                      {primarySolutionLink.name}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <a
              href="/lab"
              className="px-3 py-2 text-[11px] font-medium text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.18em] transition-colors font-mono"
            >
              Lab
            </a>
            <div className="relative group">
              <button className="px-3 py-2 text-[11px] font-medium text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.18em] transition-colors font-mono inline-flex items-center gap-1.5">
                Resources
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 pointer-events-none translate-y-1 group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 transition-all duration-200">
                <div className="min-w-[170px] rounded-xl border border-[--border] bg-[--bg-secondary] shadow-xl p-2">
                  {resourceLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="block px-3 py-2 rounded-lg text-[11px] font-medium text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-tertiary] uppercase tracking-[0.16em] font-mono"
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            <button 
              onClick={openDemoModal}
              className="px-5 py-2 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-colors flex items-center gap-2 btn-pattern"
            >
              Book a Free Audit
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <ThemeToggle />
            <button
              className="text-[--text-primary] p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain bg-[--bg-primary]/95 backdrop-blur-lg border-t border-[--border]">
          <div className="px-4 sm:px-6 py-6 space-y-4">
            <div className="pt-1">
              <p className="text-xs text-[--text-muted] uppercase tracking-[0.18em] font-mono mb-3">
                Capabilities
              </p>
              <div className="rounded-2xl border border-[--border] bg-[--bg-secondary]/55 p-3 space-y-1">
                {serviceLinks.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[--bg-tertiary] transition-colors"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigateToService(service);
                    }}
                  >
                    <p className="text-sm text-[--text-primary]">{service.name}</p>
                    <p className="text-xs text-[--text-secondary] mt-0.5">{service.summary}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="pt-2">
              <p className="text-xs text-[--text-muted] uppercase tracking-[0.18em] font-mono mb-3">
                Solution
              </p>
              <div className="rounded-2xl border border-[--border] bg-[--bg-secondary]/55 p-3 space-y-1">
                {solutionLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="block px-3 py-2 rounded-lg text-sm text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--bg-tertiary] tracking-[0.01em] transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ))}
                <div className="pt-3 mt-2 border-t border-[--border] flex justify-end">
                  <a
                    href={primarySolutionLink.href}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-medium text-[--bg-primary] uppercase tracking-[0.16em] font-mono bg-[--accent] hover:bg-[--accent-hover] transition-colors btn-pattern"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {primarySolutionLink.name}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
            <div className="pt-2">
              <a
                href="/lab"
                className="block pl-4 text-sm text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.16em] font-mono"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Lab
              </a>
            </div>
            <div className="pt-2">
              <p className="text-xs text-[--text-muted] uppercase tracking-[0.18em] font-mono mb-3">
                Resources
              </p>
              <div className="space-y-3">
                {resourceLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="block pl-4 text-sm text-[--text-secondary] hover:text-[--text-primary] uppercase tracking-[0.16em] font-mono"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
            <div className="pt-4 space-y-3">
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openDemoModal();
                }}
                className="w-full px-4 py-3 text-sm font-medium text-[--bg-primary] uppercase tracking-[0.16em] font-mono bg-[--accent] rounded-full flex items-center justify-center gap-2"
              >
                Book a Free Audit
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
